import React from "react";
import { ProgressBar } from "../progress-bar/ProgressBar";
import "./TreeMenuNew.scss";
import { Add, Minus } from "iconsax-react";
import { useTheme, Tooltip, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
// Utility function to get the total count of expanded children recursively
export const getExpandedChildrenCount = (node) => {
    if (!node.children || !node.isExpanded) {
        return 0;
    }
    let count = 0;
    for (const child of node.children) {
        count += 1; // Count the direct child
        if (child.isExpanded && child.children && child.children.length > 0) {
            count += getExpandedChildrenCount(child); // Recursively count expanded descendants
        }
    }
    return count;
};
const TreeNodeComponent = ({ node, onToggle, onNodeClick, level, isLast, hasNextSibling, siblingCount = 1, isMobile = false, onMaximizeClick, showMaximize = false, hideChildren = false, }) => {
    const handleToggle = () => {
        if (node.isExpandable) {
            onToggle(node.id);
        }
    };
    const handleNodeClick = () => {
        if (onNodeClick) {
            onNodeClick(node.id);
        }
    };
    const theme = useTheme();
    const hasChildren = node.children && node.children.length > 0;
    const showChildren = node.isExpanded && hasChildren && !hideChildren;
    // Recursively count all expanded children to get the total visible descendants
    const countAllExpandedChildren = (nodes) => {
        let count = 0;
        for (const childNode of nodes) {
            count += 1; // Count the direct child
            if (childNode.isExpanded &&
                childNode.children &&
                childNode.children.length > 0) {
                count += countAllExpandedChildren(childNode.children); // Recursively count expanded descendants
            }
        }
        return count;
    };
    // Calculate dynamic height for connection lines based on total expanded children count
    const calculateConnectionHeight = () => {
        if (!hasChildren || !node.isExpanded) {
            return 39; // Default height for nodes without expanded children
        }
        // Use the utility function to get the total number of expanded children
        const totalExpandedChildren = getExpandedChildrenCount(node);
        // Calculate height based on total number of expanded children with 8px spacing
        const childHeight = 58; // Height per visible child with 8px spacing to prevent overlap
        const baseHeight = 35; // Base offset
        return baseHeight + totalExpandedChildren * childHeight;
    };
    const connectionHeight = calculateConnectionHeight();
    // Get the total expanded children count for this node (useful for external styling or debugging)
    const totalExpandedCount = getExpandedChildrenCount(node);
    return (React.createElement("div", { className: "tree-node" },
        React.createElement("div", { className: "tree-node-content" },
            level > 0 && (React.createElement("div", { className: "connection-container", style: { left: `${(level - 1) * 24 + 16}px` } },
                React.createElement("div", { className: "vertical-line" }),
                React.createElement("div", { className: `corner-line ${isLast ? "last" : ""}`, style: {
                        "--connection-height": `${connectionHeight}px`,
                    } }))),
            React.createElement("div", { className: "tree-node-button", style: { marginLeft: `${level * 24}px` } }, node.isExpandable ? (React.createElement("button", { className: `expand-button ${node.isExpanded ? "expanded" : "collapsed"}`, onClick: handleToggle }, node.isExpanded ? React.createElement(Minus, null) : React.createElement(Add, null))) : (React.createElement("div", { className: "leaf-button" },
                React.createElement(Add, { color: theme.palette.text.disabled, size: 16, style: { cursor: "default" } })))),
            React.createElement(Tooltip, { title: node.tooltipPath || "", arrow: true, placement: "right" },
                React.createElement("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                        minWidth: 0,
                    } },
                    React.createElement("div", { className: `tree-node-info ${node.isActive ? "active" : ""}`, onClick: handleNodeClick, style: { flex: 1 } },
                        React.createElement("div", { className: "tree-node-content-wrapper" },
                            React.createElement("span", { className: "tree-node-label" }, node.label),
                            node.percentage !== undefined && (React.createElement("div", { className: "tree-node-progress" },
                                React.createElement(ProgressBar, { value: node.percentage, color: "#4a90e2", barBg: "#e8e8e8" }))))),
                    showMaximize && isMobile && onMaximizeClick ? (React.createElement(IconButton, { size: "small", sx: {
                            color: theme.palette.tertiary[40],
                        }, onClick: (e) => {
                            e.stopPropagation();
                            onMaximizeClick();
                        } },
                        React.createElement(Icon, { icon: "solar:maximize-linear", width: "20", height: "20" }))) : null))),
        showChildren && (React.createElement("div", { className: "tree-children", style: {
                "--expanded-children-count": totalExpandedCount,
            } }, node.children.map((child, index) => {
            const isLastChild = index === node.children.length - 1;
            const hasNextSiblingChild = index < node.children.length - 1;
            return (React.createElement(TreeNodeComponent, { key: child.id, node: child, onToggle: onToggle, onNodeClick: onNodeClick, level: level + 1, isLast: isLastChild, hasNextSibling: hasNextSiblingChild, siblingCount: node.children.length, isMobile: isMobile, onMaximizeClick: onMaximizeClick, showMaximize: false }));
        })))));
};
export const TreeMenuNew = ({ data, expandedNodes, activeNodeId, onToggle, onNodeClick, className = "", isMobile = false, onMaximizeClick, showOnlyFirst = false, hideChildren = false, }) => {
    // Update nodes with expansion and active state
    const updateNodeState = (nodes) => {
        return nodes.map((node) => (Object.assign(Object.assign({}, node), { isExpanded: expandedNodes.has(node.id), isActive: node.id === activeNodeId, children: node.children ? updateNodeState(node.children) : undefined })));
    };
    const updatedData = updateNodeState(data);
    const nodesToDisplay = showOnlyFirst && isMobile ? updatedData.slice(0, 1) : updatedData;
    return (React.createElement("div", { className: `tree-menu-new ${className}` }, nodesToDisplay.map((node, index) => {
        const isLast = index === nodesToDisplay.length - 1;
        const hasNextSibling = index < nodesToDisplay.length - 1;
        const showMaximize = showOnlyFirst && isMobile && index === 0;
        return (React.createElement(TreeNodeComponent, { key: node.id, node: node, onToggle: onToggle, onNodeClick: onNodeClick, level: 0, isLast: isLast, hasNextSibling: hasNextSibling, siblingCount: nodesToDisplay.length, isMobile: isMobile, onMaximizeClick: onMaximizeClick, showMaximize: showMaximize, hideChildren: hideChildren }));
    })));
};
